/**
 * QUIPWA — DBProvider
 *
 * Opens the local databases, builds the app services (lib/app-services.ts)
 * and starts the sync engine. The app is usable as soon as the local
 * databases are open — Drive and the Google session are never waited for.
 *
 * When a sync changes local data, every React Query cache is invalidated so
 * the screens re-read from SQLite.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createAppServices, type AppServices } from '../lib/app-services.ts'
import { useUserSettings } from '../lib/settings/user-settings.ts'
import { useNotesStore } from '../stores/notes-store.ts'
import { useThemeStore } from '../lib/theme/theme-store.ts'
import { DBContext, type DBContextValue } from './DBContext.ts'

const EMPTY: DBContextValue = {
  service: null,
  services: null,
  sync: null,
  isReady: false,
  error: null,
}

export function DBProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [ctx, setCtx] = useState<DBContextValue>(EMPTY)

  useEffect(() => {
    let cancelled = false
    let services: AppServices | null = null

    createAppServices({
      onLocalDataChanged: () => {
        // Another device may have changed preferences or themes too.
        if (services) {
          void useUserSettings.getState().hydrate(services.state)
          void useThemeStore.getState().hydrateFromReplica(services.state)
        }
        void queryClient.invalidateQueries()
      },
    })
      .then(async (s) => {
        services = s
        if (cancelled) {
          void s.close()
          return
        }
        await useUserSettings.getState().hydrate(s.state)
        await useThemeStore.getState().hydrateFromReplica(s.state)
        useNotesStore.getState().attach(s.notes, { refresh: () => s.sync.requestSync('notes', { urgent: true }) })
        setCtx({ service: s.state, services: s, sync: s.sync, isReady: true, error: null })
        s.sync.start()
      })
      .catch((err: unknown) => {
        console.error('[QUIPWA] Could not open the local databases', err)
        if (!cancelled) setCtx({ ...EMPTY, error: err instanceof Error ? err.message : String(err) })
      })

    return () => {
      cancelled = true
      // The notes store must not keep a repository whose database is closing.
      if (!useNotesStore.getState().mockMode) useNotesStore.getState().detach()
      if (services) void services.close()
    }
  }, [queryClient])

  return <DBContext.Provider value={ctx}>{children}</DBContext.Provider>
}
